import { Outlet, useLocation } from 'react-router';
import { adminInitials } from '@/contracts';

import { AdminHeader, type AdminNotification } from '@/components/admin/admin-header';
import { AdminSidebar } from '@/components/admin/admin-sidebar';
import { AdminMobileNav } from '@/components/admin/admin-mobile-nav';
import { ForbiddenScreen } from '@/components/admin/forbidden-screen';
import { canAccessAdminPath } from '@/components/admin/nav';
import { useLocale, useTranslations } from '@/i18n/use-locale';
import { relativeTime } from '@/lib/format-time';
import { useAdmin } from './admin-session';
import { useNotificationsQuery, usePendingCountsQuery } from '@/lib/queries';

/**
 * The console shell (لوحة الأدمن), from the approved prototype at
 * `design/kader-admin-web-app.html`: a fixed 256px sidebar on the inline start —
 * the right-hand edge under `dir="rtl"` — the content offset by `ms-64`, and a
 * sticky translucent header above it.
 *
 * It is still the one file every authenticated route renders through, and still
 * for the same reason: **a route added tomorrow is covered without anyone
 * remembering to cover it.** What moved is only where the answers come from —
 * `RequireSession` establishes there is a session, this reads the pathname from
 * the router rather than a header the middleware injected, and the badges are a
 * query rather than an `await`.
 *
 * The authorization check is unchanged and deliberate: a document auditor who
 * types `/admin/settings` gets the refusal panel, and the settings screen is
 * never rendered. The API refuses the data too — this is what stops the request
 * being made, not what makes it safe.
 */
export function AdminShell() {
  const admin = useAdmin();
  const pathname = useLocation().pathname;
  const t = useTranslations('admin');
  const { locale } = useLocale();

  // Both badges are what the queue holds right now (C5), so they are refetched
  // rather than read once — a pending count that is a minute stale is a count
  // that lies about work somebody is doing.
  const counts = usePendingCountsQuery();
  const notifications = useNotificationsQuery();

  const user = {
    name: admin.fullName,
    role: t(`roles.${admin.role}`),
    initials: adminInitials(admin.fullName),
  };

  const items: AdminNotification[] = (notifications.data ?? []).map((item) => ({
    id: item.id,
    title: t(`notifications.${item.kind}`),
    body: item.subject,
    time: relativeTime(new Date(item.createdAt), locale),
    href: item.href,
  }));

  return (
    <div className="min-h-screen font-[family-name:var(--font-admin)]">
      <AdminSidebar user={user} permissions={admin.permissions} badges={counts.data ?? {}} />

      {/* The fixed sidebar only reserves its 256px at `lg`; below it, the mobile
          drawer overlays instead, so the content uses the full width. */}
      <div className="lg:ms-64">
        <AdminHeader
          user={user}
          notifications={items}
          canSearchTalents={canAccessAdminPath('/talents', admin.permissions)}
          leading={
            <AdminMobileNav
              user={user}
              permissions={admin.permissions}
              badges={counts.data ?? {}}
            />
          }
        />
        <main className="p-6">
          {canAccessAdminPath(pathname, admin.permissions) ? (
            <Outlet />
          ) : (
            <ForbiddenScreen role={user.role} />
          )}
        </main>
      </div>
    </div>
  );
}
